import { FaceLandmarker, NormalizedLandmark } from '@mediapipe/tasks-vision';

export type FaceBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type CropConfig = {
  cropExpandTop?: number;
  cropExpandBottom?: number;
  cropExpandSide?: number;
};

export type CropResult = {
  cropX: number;
  cropY: number;
  cropWidth: number;
  cropHeight: number;
};

export const calculateSmartCrop = (
  face: FaceBox,
  vW: number,
  vH: number,
  targetRatio: number,
  ipad?: CropConfig
): CropResult => {
  const faceX = face.x * vW;
  const faceY = face.y * vH;
  const faceW = face.width * vW;
  const faceH = face.height * vH;

  const faceCenterX = faceX + faceW / 2;

  const expandTop = Number(ipad?.cropExpandTop) ?? 0.6;
  const expandBottom = Number(ipad?.cropExpandBottom) ?? 0.5;
  const expandSide = Number(ipad?.cropExpandSide) ?? 0.5;

  let desiredW = faceW * (1 + 2 * expandSide);
  let desiredH = faceH * (1 + expandTop + expandBottom);

  const currentRatio = desiredW / desiredH;

  if (currentRatio > targetRatio) {
    desiredH = desiredW / targetRatio;
  } else {
    desiredW = desiredH * targetRatio;
  }

  let cropWidth = desiredW;
  let cropHeight = desiredH;

  let cropX = faceCenterX - cropWidth / 2;

  let cropY = faceY - faceH * expandTop;

  if (cropX < 0) cropX = 0;
  if (cropY < 0) cropY = 0;

  if (cropX + cropWidth > vW) {
    cropWidth = vW - cropX;
  }
  if (cropY + cropHeight > vH) {
    cropHeight = vH - cropY;
  }

  const finalRatio = cropWidth / cropHeight;
  if (Math.abs(finalRatio - targetRatio) > 0.01) {
    if (finalRatio > targetRatio) {
      cropWidth = cropHeight * targetRatio;
    } else {
      cropHeight = cropWidth / targetRatio;
    }
  }

  return {
    cropX: Math.round(cropX),
    cropY: Math.round(cropY),
    cropWidth: Math.round(cropWidth),
    cropHeight: Math.round(cropHeight),
  };
};

interface DetectAndCropParams {
  landmarker: FaceLandmarker;
  video: HTMLVideoElement;
  lastTimestampRef: { current: number };
  faceRef: { current: NormalizedLandmark[] | null };
  vW: number;
  vH: number;
  dbRatio: number;
  cropConfig?: CropConfig;
  generateImg: (
    coords: CropResult,
    targetW: number,
    targetH: number,
    quality: number,
    mirror?: boolean
  ) => { uri: string; base64: string } | null;
  dbW: number;
  dbH: number;
  isSerproActive?: boolean;
  getCentralCrop: (ratio: number) => CropResult;
  logMediaPipe: (type: string, message: string, throttleMs: number) => void;
  describeVideo: (video: HTMLVideoElement | null) => string;
  captureFallbackNoLandmarks: () => void;
  resetOnError: (msg: string, retry?: boolean) => void;
  resetOnErrorRetry?: boolean;
}

export interface DetectAndCropOutput {
  dbResult: { uri: string; base64: string };
  serproResult: { uri: string; base64: string } | null;
}

export const detectAndCropFace = ({
  landmarker,
  video,
  lastTimestampRef,
  faceRef,
  vW,
  vH,
  dbRatio,
  cropConfig,
  generateImg,
  dbW,
  dbH,
  isSerproActive,
  getCentralCrop,
  logMediaPipe,
  describeVideo,
  captureFallbackNoLandmarks,
  resetOnError,
  resetOnErrorRetry
}: DetectAndCropParams): DetectAndCropOutput | null => {
  const finalTs = Math.max(performance.now(), lastTimestampRef.current + 1);
  lastTimestampRef.current = finalTs;
  const finalResults = landmarker.detectForVideo(video, finalTs);

  const landmarks = finalResults.faceLandmarks?.[0];
  if (!landmarks) {
    logMediaPipe('[MediaPipe] landmarks lost on final capture frame', describeVideo(video), 0);
    captureFallbackNoLandmarks();
    return null;
  }

  const landmarksForCrop = faceRef.current ?? landmarks;
  const minX = Math.min(...landmarksForCrop.map((p) => p.x));
  const maxX = Math.max(...landmarksForCrop.map((p) => p.x));
  const minY = Math.min(...landmarksForCrop.map((p) => p.y));
  const maxY = Math.max(...landmarksForCrop.map((p) => p.y));

  const dbCoords = calculateSmartCrop(
    { x: minX, y: minY, width: maxX - minX, height: maxY - minY },
    vW,
    vH,
    dbRatio,
    cropConfig
  );

  const dbResult = generateImg(dbCoords, dbW, dbH, 0.85, true);

  if (!dbResult) {
    resetOnError('invalidPhotoFacial', resetOnErrorRetry);
    return null;
  }

  const serproResult = isSerproActive
    ? generateImg(getCentralCrop(3 / 4), 960, 1280, 0.95, false)
    : null;

  return {
    dbResult,
    serproResult,
  };
};
